// 필드·균열 읽기 전용 뷰(GameView.field). 게임(game/field)이 채우고 렌더·UI는 읽기만 한다. 같은 객체를 재사용한다(프레임마다 새로 만들지 않는다).
import type { FieldNetHook } from './net';

/** 필드 이벤트 종류: 0 없음, 1 엘리트 무리, 2 보물 고블린 */
export const FieldEventKind = { None: 0, Elite: 1, Goblin: 2 } as const;
/** 균열 단계: 0 없음, 1 사냥(게이지), 2 수호자, 3 보상 상자, 4 끝(출구) */
export const RiftPhase = { None: 0, Hunt: 1, Guardian: 2, Chest: 3, Done: 4 } as const;

/** 필드 이벤트 번호(Ev 표 밖, 150번대 — specialMonster SpecialEv 90번대와 같은 방식) */
export const FieldEv = {
  /** 이벤트 시작: x,z 자리, a 종류(FieldEventKind), b 남은 초 */
  EventStart: 150,
  /** 이벤트 끝: a 종류, b 1 처치·0 사라짐, c 받은 조각, d 받은 골드 */
  EventEnd: 151,
  /** 균열 조각 획득: x,z, a 새 조각 수, b 이번에 얻은 수 */
  Shard: 152,
  /** 청크 활성 바뀜: a 영웅 청크, b 활성 청크 수 */
  Chunks: 153,
  /** 균열 입장: a 층 수, b 층 번호(0..) */
  RiftEnter: 154,
  /** 균열 층 통로 열림: x,z 자리, a 다음 층 */
  RiftPortal: 155,
  /** 균열 수호자 등장: x,z, a 체력 */
  RiftGuardian: 156,
  /** 균열 보상: a 골드, b 강화석, c 시간 안(1)/초과(0) */
  RiftReward: 157,
  /** 무리 리스폰: a 무리 번호, b 마리 */
  PackSpawn: 158,
  /** 숨은 던전 발견: x,z */
  HiddenFound: 159,
  /** 대균열 입장: a 단계, b 층 수 */
  GriftEnter: 160,
  /** 대균열 결과: a 단계, b 새 최고 기록, c 1 시간 안·0 초과, d 이정표 강화석 */
  GriftResult: 161,
} as const;

export interface FieldEventView {
  readonly kind: number;
  readonly x: number;
  readonly z: number;
  /** 남은 틱(세계 시간) */
  readonly remainTicks: number;
  /** 다음 이벤트까지 남은 틱 */
  readonly nextTicks: number;
  /** 시작할 때마다 1 증가(알림 띠가 새 이벤트를 알아챈다) */
  readonly serial: number;
  /** 지금 살아 있는 이벤트 몹 수 */
  readonly alive: number;
  /** 처치한 이벤트 수 */
  readonly cleared: number;
}
export interface RiftView {
  readonly phase: number;
  /** 0..1 */
  readonly gauge: number;
  /** 층 번호(0..)·층 수 */
  readonly floor: number;
  readonly floors: number;
  /** 경과 틱 / 제한 틱 */
  readonly elapsedTicks: number;
  readonly limitTicks: number;
  /** 다음 층 통로·보상 상자·출구 자리(active 0이면 없음) */
  readonly portalActive: boolean;
  readonly portalX: number;
  readonly portalZ: number;
  readonly chestActive: boolean;
  readonly chestX: number;
  readonly chestZ: number;
  /** 마지막 보상(골드·강화석) */
  readonly rewardGold: number;
  readonly rewardStones: number;
  readonly clears: number;
  /** 대균열(C12): 이번 판 단계(0 = 일반 균열), 최고 기록, 상한, 고를 수 있는 가장 높은 단계, 열쇠 */
  readonly tier: number;
  readonly best: number;
  readonly cap: number;
  readonly selectable: number;
  readonly keys: number;
  /** 입구 선택 띠(일반 균열 / 대균열 단계) 열림 */
  readonly choice: boolean;
  /** 마지막 대균열 결과: 0 없음, 1 시간 안 클리어(기록), 2 시간 초과(위로 상자) */
  readonly result: number;
  readonly newBest: number;
  readonly prevBest: number;
  /** 이번에 받은 10단계 이정표 강화석(없으면 0) */
  readonly milestoneStones: number;
}
export interface FieldView {
  /** 이 계정이 필드를 쓸 수 있는지(fieldUnlocked) */
  readonly unlocked: boolean;
  /** 필드·균열 안인지(장면 Field/Rift) */
  readonly active: boolean;
  readonly zoneName: string;
  /** 영웅 청크(-1 = 필드 밖) */
  readonly heroChunk: number;
  /** 활성 청크 번호(앞 activeCount개). 같은 배열을 재사용한다 */
  readonly activeChunks: Int8Array;
  readonly activeCount: number;
  /** 활성 청크가 바뀔 때마다 1 증가 */
  readonly chunkSerial: number;
  readonly shards: number;
  /** 귀환석 곁 대기(0..1) */
  readonly returnHold: number;
  /** 숨은 던전 입구를 찾았는지(미니맵 표시) */
  readonly hiddenFound: boolean;
  readonly event: FieldEventView;
  readonly rift: RiftView;
  /** 필드 몹 레벨(영웅 레벨 기준) */
  readonly mobLevel: number;
  /** 몹 무리 상태(무리 번호 순): 0 대기(청크 비활성), 1 살아 있음, 2 처치 뒤 리스폰 대기 */
  readonly packState: Uint8Array;
  readonly packCount: number;
  /** MMO 연결점(레인 M). 없으면 null — 렌더는 net?.peers만 읽는다 */
  readonly net: FieldNetHook | null;
  /** 월드 맵 창이 열렸는지(필드에서만, 열려 있는 동안 영웅 입력 차단) */
  readonly mapOpen: boolean;
}
